/**
 * ImpactStats.tsx - Fundraiser Statistics Display Component
 *
 * This component displays key fundraiser statistics in a clean, organized format
 * using data from the builder's stats configuration. Shows custom statistics
 * defined by the fundraiser creator.
 *
 * Key Features:
 * - Custom statistics display from builder configuration
 * - Responsive grid layout (2x2 or stacked)
 * - Empty state handling with helpful messaging
 * - Consistent styling with other impact components
 * - Zone-aware layout adaptation
 *
 * Data Source:
 * - Uses fundraiserStats from builder data
 * - Falls back to selected fundraiser stats
 * - Shows empty state if no stats configured
 *
 * Props:
 * - forceStacked?: boolean - Force single column layout
 *
 * Dependencies: MobX store, styled-components
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { fundraiserStore } from "../stores/FundraiserStore";

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StatCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  transition: transform 0.2s ease;
  opacity: 0;
  animation: impactStatFadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
  &:nth-child(4) {
    animation-delay: 0.4s;
  }

  @keyframes impactStatFadeIn {
    from {
      opacity: 0;
      transform: translateY(15px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 16px;
  color: #64748b;
  font-weight: 600;
`;

interface ImpactStatsProps {
  forceStacked?: boolean;
}

export const ImpactStats: React.FC<ImpactStatsProps> = observer(
  ({ forceStacked = false }) => {
    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const builderData = fundraiserStore.builderData;
    const hasLiveBuilderData =
      builderData.id &&
      selectedFundraiser &&
      builderData.id === selectedFundraiser.id;

    const stats = builderData?.fundraiserStats || [];

    const sortedStats = stats.slice().sort((a, b) => a.order - b.order);

    // Determine grid columns based on layout and number of stats
    let gridColumns;
    if (forceStacked) {
      gridColumns = 1; // Force single column for RIGHT zone
    } else {
      // Always use up to 4 columns for fundraiser stats
      gridColumns = Math.min(stats.length, 4);
    }

    return (
      <StatsContainer
        style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}
      >
        {sortedStats.map((stat) => (
          <StatCard key={stat.id}>
            <StatNumber>{stat.value}</StatNumber>
            <StatLabel>{stat.label}</StatLabel>
          </StatCard>
        ))}
      </StatsContainer>
    );
  }
);
